/**
 * EvidenceDetailModal — Phase 10
 *
 * Detail panel for an EvidenceRecord. Shows screen name, action, fingerprint,
 * verification audit trail, and exposes the verify/reject actions.
 *
 * Integrates the interactive DOM element filter from main and the screenshot
 * viewer to fulfill the user request of non-destructive merges.
 */
import React, { useState, useEffect } from 'react';
import {
    X, ShieldCheck, ShieldX, Hash, Clock, User, Activity, AlertCircle,
    ThumbsUp, EyeOff, ListChecks
} from 'lucide-react';

interface EvidenceRow {
    id: string;
    evidence_id: string;
    session_id: string;
    screen_name: string;
    screen_fingerprint: string;
    action_type: string;
    action_selector: string | null;
    result_type: string;
    evidence_status: string;
    evidence_quality: string;
    capture_mode: string;
    screenshot_path: string | null;
    dom_elements: any;
    timestamp: string | null;
    verified_by: string | null;
    verified_at: string | null;
}

interface Props {
    record: EvidenceRow;
    onClose: () => void;
    onVerify: (payload: any) => void;
    onReject: (reason: string) => void;
    actionInProgress: boolean;
}

const RESULT_TYPE_COLORS: Record<string, string> = {
    new_screen:           '#06D6A0',
    validation_state:     '#FFB703',
    error_state:          '#EF476F',
    partial_flow:         '#FF8A3D',
    duplicate_route:      '#00B4D8',
    session_end:          '#8899AA',
    no_observable_change: '#8899AA',
    navigation_return:    '#00B4D8',
    known_screen:         '#00B4D8',
};

const screenshotSrc = (path: string | null): string | null => {
    if (!path) return null;
    if (path.startsWith('data:') || path.startsWith('http')) return path;
    if (path.startsWith('iVBOR') || path.startsWith('/9j/') || path.length > 500) {
        return `data:image/png;base64,${path}`;
    }
    return path;
};

const EvidenceDetailModal: React.FC<Props> = ({ record, onClose, onVerify, onReject, actionInProgress }) => {
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [selectedElementIndices, setSelectedElementIndices] = useState<Set<number>>(new Set());

    // Initialize DOM elements selection
    useEffect(() => {
        if (record && Array.isArray(record.dom_elements)) {
            setSelectedElementIndices(new Set(record.dom_elements.map((_, i) => i)));
        } else {
            setSelectedElementIndices(new Set());
        }
    }, [record]);

    const resultColor = RESULT_TYPE_COLORS[record.result_type] ?? '#8899AA';

    const toggleElement = (index: number) => {
        const next = new Set(selectedElementIndices);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        setSelectedElementIndices(next);
    };

    const toggleAll = () => {
        if (!record || !Array.isArray(record.dom_elements)) return;
        if (selectedElementIndices.size === record.dom_elements.length) {
            setSelectedElementIndices(new Set());
        } else {
            setSelectedElementIndices(new Set(record.dom_elements.map((_, i) => i)));
        }
    };

    const submitVerify = () => {
        let payload: any = {};
        if (Array.isArray(record.dom_elements)) {
            const filtered = record.dom_elements.filter((_, i) => selectedElementIndices.has(i));
            payload = { dom_elements: filtered };
        }
        onVerify(payload);
    };

    const submitReject = () => {
        if (rejectReason.trim().length < 4) return;
        onReject(rejectReason.trim());
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 z-40"
                onClick={onClose}
            />

            {/* Split screen modal/drawer: left screenshot, right metadata & DOM elements */}
            <div className="fixed inset-x-4 inset-y-4 md:inset-x-12 md:inset-y-12 bg-slate-950 border border-slate-800 z-50 rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
                
                {/* Left Side: Screenshot Viewer */}
                <div className="flex-1 md:w-1/2 bg-black flex flex-col min-h-[300px]">
                    <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900">
                        <h3 className="text-white font-medium truncate">{record.screen_name}</h3>
                        <span className="text-xs text-slate-500 font-mono">{record.evidence_id}</span>
                    </div>
                    <div className="flex-1 overflow-auto p-4 flex items-center justify-center relative">
                        {record.screenshot_path ? (
                            <img 
                                src={screenshotSrc(record.screenshot_path)!} 
                                alt={record.screen_name} 
                                className="max-w-full max-h-[70vh] object-contain p-2"
                            />
                        ) : (
                            <p className="text-slate-500 text-sm">Visualización de screenshot no disponible</p>
                        )}
                    </div>
                </div>

                {/* Right Side: Metadata, Audits & Interactive DOM List */}
                <div className="w-full md:w-1/2 bg-slate-950 border-t md:border-t-0 md:border-l border-slate-800 flex flex-col h-full overflow-hidden">
                    {/* Header */}
                    <div className="sticky top-0 bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">
                        <div>
                            <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">
                                Review & Baseline Builder
                            </div>
                            <h2 className="text-base font-bold text-white">
                                Human-Governed QA
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Scrollable contents */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* Action row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1">
                                    <Activity className="w-3 h-3" />
                                    Acción
                                </div>
                                <div className="font-mono bg-slate-900 text-slate-200 px-3 py-2 rounded text-xs break-all">
                                    {record.action_type}
                                    {record.action_selector && (
                                        <div className="text-cyan-400 text-[11px] mt-1 break-all font-mono">
                                            {record.action_selector}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
                                    Resultado
                                </div>
                                <div
                                    className="font-mono px-3 py-2 rounded text-xs border truncate"
                                    style={{
                                        color: resultColor,
                                        backgroundColor: `${resultColor}15`,
                                        borderColor: `${resultColor}55`,
                                    }}
                                    title={record.result_type}
                                >
                                    {record.result_type}
                                </div>
                            </div>
                        </div>

                        {/* Audit card */}
                        <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 space-y-2">
                            <MetaRow
                                icon={Hash}
                                label="Fingerprint de pantalla"
                                value={<code className="text-cyan-400 font-mono text-xs break-all">{record.screen_fingerprint}</code>}
                            />
                            <MetaRow
                                icon={Clock}
                                label="Fecha Captura"
                                value={<span className="text-slate-300 text-xs">{record.timestamp ? new Date(record.timestamp).toLocaleString('es-CL') : '—'}</span>}
                            />
                        </div>

                        {/* Audit Verification badge */}
                        {(record.verified_by || record.evidence_status === 'verified') && (
                            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm">
                                        <div className="font-medium text-emerald-300">
                                            Validada para generación STRICT enterprise
                                        </div>
                                        <div className="text-slate-400 mt-1 flex items-center gap-2 text-xs">
                                            <User className="w-3 h-3" />
                                            {record.verified_by ?? 'desconocido'}
                                            {record.verified_at && (
                                                <>
                                                    <span className="text-slate-600">·</span>
                                                    {new Date(record.verified_at).toLocaleString('es-CL')}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quality message */}
                        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 text-xs text-slate-400">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    Calidad de evidencia: <strong className="text-slate-300">{record.evidence_quality}</strong>.
                                    {record.evidence_quality !== 'complete' && (
                                        <span> Se aconseja recubrir selectores en tu suite de automatización.</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* DOM Elements List (Interactive) */}
                        <div className="space-y-3">
                            <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                                <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">Elementos del DOM</h3>
                                <button 
                                    onClick={toggleAll}
                                    className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors"
                                    title="Alternar selección"
                                >
                                    <ListChecks className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                                {Array.isArray(record.dom_elements) && record.dom_elements.length > 0 ? (
                                    record.dom_elements.map((el: any, i: number) => {
                                        const isSelected = selectedElementIndices.has(i);
                                        return (
                                            <div 
                                                key={i} 
                                                onClick={() => toggleElement(i)}
                                                className={`flex items-start gap-2.5 p-2 rounded-lg border cursor-pointer transition-all ${
                                                    isSelected 
                                                        ? 'bg-blue-500/10 border-blue-500/40' 
                                                        : 'bg-slate-950 border-slate-900 opacity-40 grayscale'
                                                }`}
                                            >
                                                <div className="mt-0.5">
                                                    {isSelected
                                                        ? <ThumbsUp className="w-3.5 h-3.5 text-blue-400 fill-blue-400/20" />
                                                        : <EyeOff className="w-3.5 h-3.5 text-slate-600" />
                                                    }
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 justify-between">
                                                        <span className={`text-xs font-medium truncate ${isSelected ? 'text-white' : 'text-slate-500 line-through'}`}>
                                                            {el.visibleText || '—'}
                                                        </span>
                                                        {el.isInteractive && (
                                                            <span className={`text-[8px] uppercase font-bold px-1.5 py-0.5 rounded ${
                                                                isSelected ? 'text-blue-400 bg-blue-500/10' : 'text-slate-700 bg-slate-900'
                                                            }`}>Interactive</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1 mt-1 text-[10px]">
                                                        <code className={`font-mono ${isSelected ? 'text-cyan-400' : 'text-slate-700'}`}>
                                                            {el.elementId}
                                                        </code>
                                                        <span className="text-slate-600">·</span>
                                                        <span className="text-slate-500">{el.elementType}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-slate-600 text-xs italic">No hay selectores DOM capturados en esta pantalla.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sticky Footer actions */}
                    <div className="sticky bottom-0 bg-slate-950 border-t border-slate-800 p-6 flex items-center justify-between gap-3 z-10">
                        {showRejectForm ? (
                            <div className="w-full space-y-3">
                                <label className="block text-xs font-medium text-slate-300">
                                    Razón de rechazo (mínimo 4 caracteres)
                                </label>
                                <textarea
                                    value={rejectReason}
                                    onChange={e => setRejectReason(e.target.value)}
                                    rows={2}
                                    className="w-full bg-slate-900 border border-slate-700 rounded p-2 text-slate-200 text-xs focus:border-red-500 focus:outline-none"
                                    placeholder="Ej: selectores no correspondientes a la vista real"
                                />
                                <div className="flex gap-2">
                                    <button
                                        onClick={submitReject}
                                        disabled={rejectReason.trim().length < 4 || actionInProgress}
                                        className="px-4 py-2 bg-red-500/20 text-red-300 border border-red-500/40 rounded text-xs hover:bg-red-500/30 disabled:opacity-50 transition-colors"
                                    >
                                        Confirmar rechazo
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowRejectForm(false);
                                            setRejectReason('');
                                        }}
                                        className="px-4 py-2 bg-slate-800 text-slate-300 rounded text-xs hover:bg-slate-700 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                {record.evidence_status !== 'verified' && (
                                    <button
                                        onClick={submitVerify}
                                        disabled={actionInProgress}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded text-xs hover:bg-emerald-500/30 disabled:opacity-50 transition-colors"
                                    >
                                        <ShieldCheck className="w-4 h-4" />
                                        Validar para STRICT
                                    </button>
                                )}
                                {record.evidence_status !== 'rejected' && (
                                    <button
                                        onClick={() => setShowRejectForm(true)}
                                        disabled={actionInProgress}
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-300 border border-red-500/40 rounded text-xs hover:bg-red-500/30 disabled:opacity-50 transition-colors"
                                    >
                                        <ShieldX className="w-4 h-4" />
                                        Rechazar
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className="ml-auto px-4 py-2 text-slate-400 hover:text-slate-200 text-xs"
                                >
                                    Cerrar
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

function MetaRow({
    icon: Icon,
    label,
    value,
}: {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
}) {
    return (
        <div className="flex items-start gap-3 text-xs">
            <Icon className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] text-slate-500 uppercase tracking-wide">{label}</div>
                <div className="mt-0.5">{value}</div>
            </div>
        </div>
    );
}

export default EvidenceDetailModal;
